/**
 * Location Add Page
 * Add a new location to an organization
 */

import React from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Card from '../components/ui/Card';
import LoadingSpinner from '../components/common/LoadingSpinner';
import LocationForm from '../components/organizations/LocationForm';
import { getOrganization } from '../services/organizations.service';

const LocationAdd: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: organization, isLoading } = useQuery({
    queryKey: ['organization', id],
    queryFn: () => getOrganization(id!),
    enabled: !!id,
  });

  const handleSuccess = () => {
    navigate(`/organizations/${id}`);
  };

  const handleCancel = () => {
    navigate(`/organizations/${id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    );
  }

  // No locations may be added to a disabled (archived) organization — redirect to its detail page.
  if (organization?.is_archived) {
    return <Navigate to={`/organizations/${id}`} replace />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(`/organizations/${id}`)}
          className="text-gray-600 hover:text-gray-900 transition-colors"
          aria-label="Back to organization"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Add Location</h1>
          {organization && (
            <p className="text-gray-600 mt-1">{organization.name}</p>
          )}
        </div>
      </div>

      {/* Form Card */}
      <Card>
        <LocationForm
          organizationId={id!}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </Card>
    </div>
  );
};

export default LocationAdd;
